import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Observable, forkJoin, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { UserProfileService } from 'sigv-library';
import { RbacService, SystemViewModel, ToDoListViewModel, UserProfileService as UserProfileApiService } from 'src/app/api-info/system-management-api';
import { SYSTEM_GROUP_CODE } from '../constants/global-constants.constant';
import { UseRole } from '../enums/use-role.enum';
import { MenuInfo, TodoMenuInfo } from '../models/menu-info';
import { isNullOrEmpty } from '../utils/common-functions';
import { ReplaceWordService } from './replace-word.service';
import { SystemEnvService } from './system-env.service';
import { UserRoleService } from './user-role.service';

export const TODO_DATA_PROVIDERS = new InjectionToken<MenuItemProcessor[]>('TODO_MENU_PROVIDERS');

@Injectable({
  providedIn: 'root'
})
export class MenuProcessorService {

  constructor(@Inject(TODO_DATA_PROVIDERS) private todoMenuProcessor: MenuItemProcessor[]) { }

  prettfyMenus(menus: MenuInfo[]): Observable<MenuInfo[]> {
    return from(this.doProcessorTasks(menus));
  }

  private async doProcessorTasks(menus: MenuInfo[]): Promise<MenuInfo[]> {
    let tmpMenus: MenuInfo[] = menus;
    for (let processor of this.todoMenuProcessor) {
      tmpMenus = await processor.executeMenuProcessor(tmpMenus).toPromise();
    }
    return tmpMenus;
  }
}

@Injectable()
export class TodoMenuItemProcessor implements MenuItemProcessor {

  systemInfos: SystemViewModel[] = [];

  constructor(
    private rbacApiService: RbacService,
    private userProfileApiService: UserProfileApiService,
    private userProfileService: UserProfileService,
    private userRoleService: UserRoleService,
    private replaceWord: ReplaceWordService,
    private systemEnvService: SystemEnvService) {
    this.systemInfos = this.systemEnvService.systemList.filter(x => x.systemGroup === SYSTEM_GROUP_CODE);
  }

  executeMenuProcessor(menus: MenuInfo[]): Observable<MenuInfo[]> {
    const userRole =
      this.userProfileService.currentUserValue?.extranetUserRole || 0;

    if (
      userRole === Number(UseRole.Dealer) ||
      userRole == Number(UseRole.Agent)
    ) {
      return of(menus);
    }

    const observable = new Observable<MenuInfo[]>(observer => {
      const userNo = this.userProfileService.currentUserValue?.userNo;
      this.rbacApiService.rbacCompanyGet()
        .pipe(
          switchMap(companies => forkJoin(companies
            .filter(x => isNullOrEmpty(x.companyId) === false)
            .reduce((array, company) => {
              const apis = this.systemInfos.map(system => this.userProfileApiService.rbacUserProfileUserNoCompanyCompanyIdSystemsSystemIdToDoListsGet(userNo!, company.companyId!, system.systemId!, false, 1, false));
              return array.concat(apis);
            }, [] as Observable<ToDoListViewModel[]>[]))),
          map(todoGroups => todoGroups.reduce((sum, array) => {
            return sum.concat(array);
          }), [] as ToDoListViewModel[])
        )
        .subscribe({
          next: results => {
            const todoGroupObj = results.groupBy(x => `${x.processDefKey} ${x.taskDefKey}`);
            const todoMenuItem = menus.find(x => x.data?.functionCode?.toLowerCase().startsWith('extodo')) as TodoMenuInfo;
            if (todoMenuItem) {
              const todoSubMenus: MenuInfo[] = [];
              Object.entries(todoGroupObj).forEach(item => {
                const [key, todos] = item;
                const todo = todos.first();
                const displayLabel = `${todo.processName} ${todo.taskName}`;
                const menuInfo: TodoMenuInfo = {
                  count: todos.reduce((sum, todo) => sum += (todo.count ?? 0), 0),
                  srcLabel: displayLabel,
                  label: this.replaceWord.Replace(displayLabel),
                  entryUrl: todos[0].url,
                  data: {
                    processDefKey: todo.processDefKey,
                    processName: todo.processName,
                    taskDefKey: todo.taskDefKey,
                    taskName: todo.taskName,
                    functionCode: todo.functionCode,
                    functionName: todo.functionName,
                    systemCode: todo.systemCode,
                    systemName: todo.systemName,
                    systemFunctionId: todo.systemFunctionId,
                  }
                };
                todoSubMenus.push(menuInfo);
              });

              const todoCount = todoSubMenus.reduce((sum, menu: TodoMenuInfo) => {
                return sum + (menu.count ?? 0);
              }, 0);
              todoMenuItem.items = [...(todoMenuItem.items ?? []), ...todoSubMenus];
              todoMenuItem.count = (todoMenuItem.count ?? 0) + todoCount;
            }

            observer.next(menus);
            observer.complete();
          },
          error: err => {
            observer.error(err);
            observer.complete();
          }
        });
    });
    return observable;
  }

}

export interface MenuItemProcessor {
  executeMenuProcessor(menus: MenuInfo[]): Observable<MenuInfo[]>;
}
